import { Page } from '@adapters/navigation/page';
import { TaggingMetricMapper } from '@adapters/tagging/taggingMetricMapper';
import { Injectable } from '@angular/core';
import { ErrorMetric, PageChapter2, PageMetric, PageType } from '@core/model/domain/taggingTracker';

export type TagCommanderMapperInitializationData = { userStatus: string; prodName: string };

@Injectable({
  providedIn: 'root',
})
export class TagCommanderMetricMapper implements TaggingMetricMapper {
  private isInitialized: boolean;
  private userStatus!: string;
  private prodName!: string;

  constructor() {
    this.isInitialized = false;
  }

  private initialize(data: TagCommanderMapperInitializationData) {
    if (this.isInitialized) {
      throw new Error('TagCommanderMapper is already initialized');
    }

    this.userStatus = data.userStatus;
    this.prodName = data.prodName;

    this.isInitialized = true;
  }

  async ensureInitialization(data: TagCommanderMapperInitializationData) {
    if (this.isInitialized) {
      return;
    }

    this.initialize(data);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errorMetricMapper(payload?: any): ErrorMetric {
    this.throwIfNotInitialized();

    return {
      error_page: payload,
      error_category: 'technical',
      error_type: '',
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pageMetricMapper(page: Page, payload?: any): PageMetric {
    this.throwIfNotInitialized();

    const pageChapter3 = this.mapPageChapter3(page, payload);

    return {
      page_type: this.mapPageType(page),
      page_chapter1: 'form',
      page_chapter2: this.mapPageChapter2(page),
      page_chapter3: pageChapter3,
      page: `${this.prodName}-${this.userStatus}-${pageChapter3}`,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapPageType(page: Page): PageType {
    switch (page) {
      case 'CONSENT':
        return 'compliance';

      case 'ABANDON_BANK_CONNECTION':
      case 'CLOSE_EXTERNAL_BANK_CONNECTION_TAB':
        return 'popin';

      case 'BANK_CHOICE':
        return 'bank_detail';

      case 'WAIT_ON_EXTERNAL_BANK_LOGIN':
      case 'REDIRECT_TO_EXTERNAL_BANK_WAIT':
      case 'REDIRECT_TO_EXTERNAL_BANK_ERROR':
      case 'CONNECTION_EXTERNAL_BANK_KO':
      case 'CONNECTION_EXTERNAL_BANK_OK':
      case 'BANK_CONNECTION_FAILURE_RETRY':
      case 'SELECT_REGIONAL_BANK':
      case 'NONE_ACCOUNTS':
      case 'ACCOUNT_LOADER':
      case 'SELECT_ACCOUNTS':
        return 'authentification_process';

      case 'ACCOUNTS_ANALYSIS':
        return 'result_page';

      case 'ERROR':
        return 'error_page';

      case 'LOADING':
        return 'authentification_process';

      default:
        this.neverCase(page);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapPageChapter2(page: Page): PageChapter2 {
    switch (page) {
      case 'CONSENT':
        return 'open_banking';

      case 'BANK_CHOICE':
        return 'open_banking_start';

      case 'WAIT_ON_EXTERNAL_BANK_LOGIN':
      case 'CONNECTION_EXTERNAL_BANK_KO':
      case 'CONNECTION_EXTERNAL_BANK_OK':
      case 'BANK_CONNECTION_FAILURE_RETRY':
      case 'REDIRECT_TO_EXTERNAL_BANK_WAIT':
      case 'REDIRECT_TO_EXTERNAL_BANK_ERROR':
      case 'SELECT_REGIONAL_BANK':
      case 'CLOSE_EXTERNAL_BANK_CONNECTION_TAB':
        return 'web_authentification';

      case 'NONE_ACCOUNTS':
      case 'SELECT_ACCOUNTS':
      case 'ACCOUNT_LOADER':
        return 'authentification_success';

      case 'ACCOUNTS_ANALYSIS':
        return 'open_banking_result';

      case 'ABANDON_BANK_CONNECTION':
        return 'authentification_success';

      case 'ERROR':
        return 'open_banking_error';

      case 'LOADING':
        return 'open_banking_start';

      default:
        this.neverCase(page);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapPageChapter3(page: Page, payload: any): string {
    switch (page) {
      case 'CONSENT':
        return 'consent';
      case 'BANK_CHOICE':
        return 'bank_selection';
      case 'WAIT_ON_EXTERNAL_BANK_LOGIN':
        return 'bank_connexion_loading';
      case 'CONNECTION_EXTERNAL_BANK_KO':
        return 'bank_connexion_failure';
      case 'CONNECTION_EXTERNAL_BANK_OK':
        return 'bank_connexion_success';
      case 'BANK_CONNECTION_FAILURE_RETRY':
        return 'bank_connexion_failure_retry';
      case 'REDIRECT_TO_EXTERNAL_BANK_ERROR':
        return 'bank_redirection_failure';

      case 'SELECT_REGIONAL_BANK':
        return 'regional_bank_selection';

      case 'NONE_ACCOUNTS':
        return 'no_account_found';

      case 'SELECT_ACCOUNTS':
        if (payload.isMultipleAccount) {
          return 'multiple_accounts_proposition';
        }
        return 'one_account_proposition';

      case 'ACCOUNT_LOADER': {
        {
          if (payload.isMultipleAccount) {
            return 'multiple_accounts_loading';
          }
        }
        return 'one_account_loading';
      }

      case 'ACCOUNTS_ANALYSIS':
        switch (payload.accountAnalysisState) {
          case 'SUCCESS':
            return 'account_connexion_success';
          case 'RETRY_ACCOUNT':
            return 'account_connexion_failure_1';
          case 'INSUFFICIENT_INCOME':
            return 'account_connexion_revenue_failure';
          case 'FAILED':
            return 'account_connexion_failure_2';
          default:
            throw new Error('unhandled case');
        }

      case 'ABANDON_BANK_CONNECTION':
        return 'drop_out';

      case 'CLOSE_EXTERNAL_BANK_CONNECTION_TAB':
        return 'drop_out';

      case 'REDIRECT_TO_EXTERNAL_BANK_WAIT':
        return 'bank_redirection_loading';

      case 'ERROR':
        return 'error';

      case 'LOADING':
        return 'LOADING';

      default:
        this.neverCase(page);
    }
  }

  private neverCase(x: never): never {
    throw new Error(`this default case error : ${x}`);
  }

  private throwIfNotInitialized() {
    if (!this.isInitialized) {
      throw new Error('Tagging is not initialized');
    }
  }
}
